// InsuranceCard — the compliance policy table: what cover is held, when it
// lapses, and whether anything needs chasing.
//
// Coverage values carry NO GST label. That is a deliberate exception to the
// "every dollar on a contract surface shows its GST basis" rule and matches
// the Insurance tab: a policy limit is an indemnity ceiling set by an
// insurer, not contract consideration, so ex-GST/inc-GST is not a meaningful
// distinction to draw on it.
//
// Expiry proximity is humanised through the shared helper, so a lapsed policy
// reads "Expired 12d ago" rather than counting down past zero into "in -12
// days" — the server's `daysToExpiry` is signed.
//
// `condition` is mapped onto the existing insurance badge vocabulary rather
// than re-derived. The mapping is total over the four wire values, so an
// unhandled condition is a compile error rather than a blank cell.

import MetricCard from "../tracking/deliverable/MetricCard";
import TrackingStatusBadge from "../tracking/shared/TrackingStatusBadge";
import ReportSection, {
  NothingToReport,
  UnavailableNotice,
} from "./ReportSection";
import { countTone, expiryMeta } from "./reportFormat";
import { formatAud0dp } from "../../../utils/formatCurrency";
import { formatDayMonthYear } from "../../../utils/formatShortDate";
import { INSURANCE_TYPE_LABELS } from "../../../types";
import type { InsuranceStatus } from "../../../types";
import type {
  InsuranceCondition,
  SectionUnavailable,
  SummaryInsuranceRow,
  SummaryInsuranceSection,
} from "../../../types/report";

const SECTION_LABEL = "Insurance compliance";

/** Display mapping, not a derivation — the server has already decided the
 *  condition; this only names the badge that shows it. */
const CONDITION_BADGE: Record<InsuranceCondition, InsuranceStatus> = {
  expired: "expired",
  "expiring-soon": "expiring",
  current: "current",
  upcoming: "upcoming",
};

export interface InsuranceCardProps {
  section: SummaryInsuranceSection | SectionUnavailable;
}

export default function InsuranceCard({ section }: InsuranceCardProps) {
  if (section.status === "unavailable") {
    return (
      <ReportSection id="insurance" title={SECTION_LABEL}>
        <UnavailableNotice label={SECTION_LABEL} />
      </ReportSection>
    );
  }

  if (section.rows.length === 0) {
    return (
      <ReportSection id="insurance" title={SECTION_LABEL}>
        <NothingToReport />
      </ReportSection>
    );
  }

  return (
    <ReportSection id="insurance" title={SECTION_LABEL}>
      <div className="grid grid-cols-2 gap-3">
        <MetricCard
          testId="insurance-expired"
          label="Expired"
          value={section.expiredCount}
          valueTone={countTone(section.expiredCount, "danger")}
        />
        <MetricCard
          testId="insurance-expiring"
          label="Expiring soon"
          value={section.expiringSoonCount}
          valueTone={countTone(section.expiringSoonCount, "warning")}
        />
      </div>

      <div
        className="mt-4 overflow-x-auto"
        tabIndex={0}
        role="group"
        aria-label="Insurance and compliance policies held"
      >
        <table className="w-full">
          <caption className="sr-only">
            Insurance and compliance policies held
          </caption>
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800">
              <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                Policy
              </th>
              <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                Provider
              </th>
              <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                Status
              </th>
              <th className="px-3 py-2 text-right text-overline uppercase text-slate-600 dark:text-slate-400">
                Coverage
              </th>
              <th className="px-3 py-2 text-left text-overline uppercase text-slate-600 dark:text-slate-400">
                Expires
              </th>
            </tr>
          </thead>
          <tbody>
            {section.rows.map((row) => (
              <InsuranceRow key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      </div>
      {section.truncated && (
        <p
          data-testid="insurance-truncation-note"
          className="mt-2 text-caption text-slate-500 dark:text-slate-400"
        >
          Showing {section.rows.length} of {section.totalCount} policies
        </p>
      )}
    </ReportSection>
  );
}

function InsuranceRow({ row }: { row: SummaryInsuranceRow }) {
  const typeLabels = (row.insuranceTypes ?? [])
    .map((t) => INSURANCE_TYPE_LABELS[t])
    .join(", ");
  return (
    <tr className="border-b border-slate-100 dark:border-slate-700">
      <td
        data-testid={`insurance-policy-${row.id}`}
        className="px-3 py-2 text-body-sm text-slate-900 dark:text-slate-100"
      >
        {/* A record with neither a subject name nor a type would otherwise
            leave the row's identifying cell blank, which reads as a rendering
            fault rather than as missing data. */}
        {row.subjectName ?? (typeLabels === "" ? "—" : typeLabels)}
        {row.policyNumber !== null && (
          <span className="block font-mono text-caption text-slate-500 dark:text-slate-400">
            {row.policyNumber}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-body-sm text-slate-900 dark:text-slate-100">
        {row.provider === null ? "—" : row.provider}
      </td>
      <td className="px-3 py-2">
        <TrackingStatusBadge
          kind="insurance"
          status={CONDITION_BADGE[row.condition]}
          superseded={row.superseded}
        />
      </td>
      <td
        data-testid={`insurance-coverage-${row.id}`}
        className="px-3 py-2 text-right text-body-sm tabular-nums text-slate-900 dark:text-slate-100"
      >
        {row.coverageValue === null ? "—" : formatAud0dp(row.coverageValue)}
      </td>
      <td
        data-testid={`insurance-expiry-${row.id}`}
        className="px-3 py-2 text-body-sm tabular-nums text-slate-900 dark:text-slate-100"
      >
        {row.expiryDate === null ? (
          "—"
        ) : (
          <>
            {formatDayMonthYear(row.expiryDate)}
            {row.daysToExpiry !== null && (
              <span className="block text-caption text-slate-500 dark:text-slate-400">
                {expiryMeta(row.daysToExpiry)}
              </span>
            )}
          </>
        )}
      </td>
    </tr>
  );
}
